import React, { useState } from 'react';
import { X, Upload, Download, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { fetchPostInfo, getSavedTrackers, saveTracker } from '@/utils/instagramTrackerApi';

const isYouTubeUrl = (url) =>
  url.includes('youtube.com') || url.includes('youtu.be');

const isInstagramUrl = (url) =>
  url.includes('instagram.com/p/') || url.includes('instagram.com/reel/');

export default function AdImportModal({ onClose, influencers }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [logs, setLogs] = useState([]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.name.endsWith('.csv')) {
      setFile(selected);
    } else {
      toast.error('Please upload a valid .csv file');
    }
  };

  const handleStartImport = async () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const rows = text.split('\n').map(r => r.trim()).filter(r => r && r.includes(','));
      const dataRows = rows.slice(1); // skip header

      if (dataRows.length === 0) {
        toast.error('No data found in CSV');
        return;
      }

      setImporting(true);
      setProgress({ current: 0, total: dataRows.length });
      const newLogs = [];

      for (let i = 0; i < dataRows.length; i++) {
        const cols = dataRows[i].split(',').map(s => s.trim().replace(/"/g, ''));
        // Format: handle, video_url  (platform auto-detected from URL)
        const handleRaw = cols[0] || '';
        const url = cols[1] || '';
        const handle = handleRaw.replace('@', '').toLowerCase();

        setProgress({ current: i + 1, total: dataRows.length });

        if (!handle || !url) {
          newLogs.push({ handle: handle || '?', status: 'error', msg: 'Missing handle or URL' });
          setLogs([...newLogs]);
          continue;
        }

        try {
          if (isYouTubeUrl(url)) {
            // YouTube video — find influencer by youtube_handle or instagram_handle
            const creator = influencers.find(inf =>
              inf.youtube_handle?.replace('@', '').toLowerCase() === handle ||
              inf.instagram_handle?.replace('@', '').toLowerCase() === handle
            );

            if (!creator) {
              newLogs.push({ handle, status: 'error', msg: 'Creator not found in database' });
              setLogs([...newLogs]);
              continue;
            }

            const vRes = await api.fetchYouTubeVideo(url);
            const videoData = vRes.data;

            // Load existing tracked videos and append
            const existingRes = await api.getInfluencer(creator.id).catch(() => ({ data: creator }));
            const existing = existingRes.data?.youtube_tracked_videos || creator.youtube_tracked_videos || [];

            if (existing.some(v => v.video_id === videoData.video_id)) {
              newLogs.push({ handle, status: 'info', msg: 'Video already tracked' });
            } else {
              const updated = [videoData, ...existing];
              await api.updateYouTubeTrackedVideos(creator.id, updated);
              newLogs.push({ handle, status: 'success', msg: `YT: ${videoData.title?.slice(0, 30)}…` });
            }

          } else if (isInstagramUrl(url)) {
            // Instagram reel/post — existing logic
            const creator = influencers.find(inf =>
              inf.instagram_handle?.replace('@', '').toLowerCase() === handle
            );

            if (!creator) {
              newLogs.push({ handle, status: 'error', msg: 'Creator not found in database' });
              setLogs([...newLogs]);
              continue;
            }

            const postData = await fetchPostInfo(url);
            const trackers = await getSavedTrackers();
            const currentData = trackers[handle] || { profile: {}, posts: [], computed: {}, trackedAds: [] };
            const existingAds = currentData.trackedAds || [];

            if (existingAds.some(a => a.id === postData.id || a.url === postData.url)) {
              newLogs.push({ handle, status: 'info', msg: 'Ad already tracked' });
            } else {
              await saveTracker(handle, {
                ...currentData,
                trackedAds: [...existingAds, { ...postData, tracked_at: new Date().toISOString() }],
              });
              newLogs.push({ handle, status: 'success', msg: `IG: ${postData.format || 'Post'} tracked` });
            }

          } else {
            newLogs.push({ handle, status: 'error', msg: 'Unrecognised URL (not Instagram or YouTube)' });
          }

          if (i < dataRows.length - 1) await new Promise(r => setTimeout(r, 700));
        } catch (err) {
          newLogs.push({ handle, status: 'error', msg: err.message?.slice(0, 50) || 'Unknown error' });
        }

        setLogs([...newLogs]);
      }

      setImporting(false);
      toast.success('Batch import complete');
    };
    reader.readAsText(file);
  };

  // Build a sample CSV for download
  const handleDownloadTemplate = () => {
    const csv = `handle,video_url\n@khushibalott,https://youtube.com/shorts/g_T0J9Lb5YI\n@johndoe,https://instagram.com/reel/ABC123\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'ads_import_template.csv';
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-salty-card border border-salty-border rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-salty-border">
          <div>
            <h2 className="text-xl font-bold text-salty-text-main">Import Campaign Ads</h2>
            <p className="text-xs text-salty-text-muted mt-0.5">
              Bulk track Instagram reels &amp; YouTube videos for multiple influencers.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-salty-text-muted hover:text-salty-text-main transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!importing && logs.length === 0 ? (
            <>
              {/* CSV format guide */}
              <div className="bg-salty-bg/50 border border-salty-border rounded-xl p-4 text-xs text-salty-text-muted space-y-1.5">
                <p className="font-bold text-salty-text-main text-sm mb-2">CSV Format</p>
                <p><code className="bg-salty-bg px-1 rounded">handle, video_url</code> — one row per video</p>
                <p>• Platform is auto-detected from the URL</p>
                <p>• Instagram: <code className="bg-salty-bg px-1 rounded">instagram.com/reel/...</code> or <code className="bg-salty-bg px-1 rounded">instagram.com/p/...</code></p>
                <p>• YouTube: <code className="bg-salty-bg px-1 rounded">youtube.com/watch?v=...</code> or <code className="bg-salty-bg px-1 rounded">youtube.com/shorts/...</code></p>
              </div>

              <div className="bg-salty-bg/50 border border-dashed border-salty-border rounded-xl p-8 text-center">
                <input type="file" id="ad-csv" className="hidden" accept=".csv" onChange={handleFileChange} />
                <label htmlFor="ad-csv" className="cursor-pointer flex flex-col items-center gap-3">
                  <div className="w-12 h-12 bg-salty-purple/10 text-salty-purple rounded-full flex items-center justify-center">
                    <Upload className="w-6 h-6" />
                  </div>
                  <div className="text-sm">
                    {file
                      ? <span className="text-emerald-400 font-bold">{file.name}</span>
                      : <span className="text-salty-text-muted">Drop CSV here or <span className="text-salty-purple">browse</span></span>
                    }
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-between py-2.5 px-4 bg-salty-bg/50 rounded-lg border border-salty-border">
                <span className="text-xs text-salty-text-muted flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Need a template?
                </span>
                <button onClick={handleDownloadTemplate} className="text-xs font-bold text-salty-purple hover:underline uppercase tracking-wide">
                  Download CSV Template
                </button>
              </div>

              <Button onClick={handleStartImport} className="w-full bg-salty-purple hover:bg-salty-purple-dark py-6 text-base font-bold" disabled={!file}>
                Track All Ads
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-salty-text-main">Import Status</span>
                <span className="text-xs text-salty-text-muted">{progress.current} of {progress.total} processed</span>
              </div>
              <div className="h-2 bg-salty-bg/50 rounded-full overflow-hidden border border-salty-border">
                <div
                  className="h-full bg-salty-purple transition-all duration-300"
                  style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>

              <div className="max-h-[260px] overflow-y-auto border border-salty-border rounded-lg bg-salty-bg/50 p-2 space-y-1 scrollbar-hide">
                {logs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 text-[11px] border-b border-salty-border/40 last:border-0">
                    <span className="text-salty-text-main font-medium">@{log.handle}</span>
                    <div className={`flex items-center gap-1 font-bold ${
                      log.status === 'success' ? 'text-emerald-400' :
                      log.status === 'error' ? 'text-rose-400' : 'text-amber-400'
                    }`}>
                      {log.status === 'success' && <CheckCircle2 className="w-3 h-3" />}
                      {log.status === 'error' && <AlertCircle className="w-3 h-3" />}
                      {log.msg}
                    </div>
                  </div>
                ))}
                {importing && (
                  <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-salty-text-muted">
                    <Loader2 className="w-3 h-3 animate-spin" /> Processing…
                  </div>
                )}
              </div>

              {!importing && (
                <Button onClick={onClose} variant="outline" className="w-full border-salty-border text-salty-text-muted hover:text-salty-text-main">
                  Done
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
